/* Camps.jsx — Full camp hub: 6 sites grid + multi-step register form */

/* Season switch for the WHOLE camp season, distinct from a single site's
   `registrationClosed` (which means that one site is full). It is the CMS key
   camps.registration_open, flipped from the dashboard's Camp Registrations
   tab, and /api/register-camp refuses submissions while it is off -- hiding
   the buttons here is the courtesy, the server is the lock.
   Fails CLOSED: anything but a real true reads as closed. */
function campRegistrationOpen() {
  const v = (window.CMS || { get: () => false }).get("camps.registration_open", false);
  return v === true || v === "true";
}

function campSeasonYear() {
  return String((window.CMS || { get: (_k, fb) => fb }).get("camps.season_year", "2027"));
}

function campRegistrationFee() {
  const raw = String((window.CMS || { get: () => "" }).get("camps.registration_fee_dollars", "")).trim();
  return /^\d+(\.\d{1,2})?$/.test(raw) && Number(raw) > 0 ? `$${raw}` : "";
}

// window.CMS fills in after first paint; re-render the page once it has.
function useCmsReady() {
  const [, bump] = React.useState(0);
  React.useEffect(() => {
    if (window.CMS && window.CMS._ready) return;
    const on = () => bump(n => n + 1);
    window.addEventListener("cms:ready", on);
    return () => window.removeEventListener("cms:ready", on);
  }, []);
}

function CampsPage({ setRoute }) {
  useCmsReady();
  const [sites, setSites] = React.useState(window.CAMP_SITES || []);

  React.useEffect(() => {
    let cancelled = false;
    fetch("/api/camps")
      .then(r => r.ok ? r.json() : null)
      .then(j => {
        if (cancelled) return;
        if (j && j.ok && Array.isArray(j.rows) && j.rows.length > 0) {
          setSites(j.rows.map(r => ({
            id: r.slug,
            name: r.name,
            region: r.region || "",
            type: r.type,
            ageRange: r.age_range,
            address: r.address,
            coords: (r.lat != null && r.lng != null) ? [Number(r.lat), Number(r.lng)] : null,
            color: r.type === "YPTC" ? "var(--c-plum)" : "var(--c-primary)",
            startDate: r.start_date,
            endDate: r.end_date,
            hours: r.hours,
            feeCents: r.fee_cents,
            registrationClosed: !!r.registration_closed
          })));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // The registration page builds its site list from window.CAMP_SITES.
  React.useEffect(() => { window.CAMP_SITES = sites; }, [sites]);

  // Registration is the full participant form with the waivers, so it gets
  // its own page (/#register in camp mode) rather than a modal.
  const openRegister = (camp) => {
    if (!campRegistrationOpen()) return;
    window.location.hash = `#register?camp=${encodeURIComponent(camp.id)}`;
    window.scrollTo(0, 0);
  };

  return (
    <div>
      <CampsHero />
      <CampProcess />
      <CampGrid sites={sites} onRegister={openRegister} />
      <CampFAQ />
    </div>
  );
}

/* Two faces, one per state of the season switch. Open: point families at the
   sites, and say up front that a registration is pending until the fee is
   paid. Closed: offer the mailing list instead, posting to the same /api/loop
   endpoint the home page newsletter uses, tagged with its own source so J.J.
   can tell camp interest apart from general subscribers. */
function CampsHero() {
  const open = campRegistrationOpen();
  const year = campSeasonYear();
  const fee = campRegistrationFee();
  const scrollTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const [email, setEmail] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [error, setError] = React.useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch("/api/loop", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: `camps_${year}_interest` }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) throw new Error(json.error || `error_${res.status}`);
      setSubmitted(true);
    } catch (_err) {
      setError("Couldn't add you right now. Try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section style={{ background: "var(--c-primary)", color: "var(--c-paper)", borderBottom: "2px solid var(--c-ink)", padding: "80px 0 64px" }}>
      <div className="container">
        <div className="eyebrow" style={{ color: "var(--c-paper)", opacity: 0.8 }}>Summer Camp {year}</div>
        <h1 className="display" style={{ fontSize: "clamp(40px, 7.4vw, 108px)", color: "var(--c-paper)", margin: "12px 0 24px", maxWidth: 1100, lineHeight: 0.98 }}>
          {open ? <>Registration is open<br />for {year}.</> : <>{year} registration<br />opens soon.</>}
        </h1>
        <p style={{ fontSize: 19, maxWidth: 640, lineHeight: 1.5, opacity: 0.95 }}>
          {open
            ? <>Pick the site you want below and register online. No need to go to the site to sign up. Your
                camper&rsquo;s spot is held once the {fee ? `${fee} ` : ""}registration
                fee is paid to our office.</>
            : <>Leave your email and we&rsquo;ll let you know as soon as {year}{" "}
                registration opens. Camp details, locations and the full process
                are below.</>}
        </p>

        {open ? null : !submitted ? (
          <form onSubmit={onSubmit} style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 28, maxWidth: 520 }}>
            {/* Visually hidden, not display:none -- the input still needs an
                accessible name. There is no .sr-only class on this site. */}
            <label htmlFor="camps-interest-email" style={{
              position: "absolute", width: 1, height: 1, overflow: "hidden",
              clip: "rect(0 0 0 0)", whiteSpace: "nowrap",
            }}>
              Email address
            </label>
            <input
              id="camps-interest-email"
              type="email"
              required
              placeholder="you@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
              style={{
                flex: 1,
                minWidth: 240,
                padding: "16px 18px",
                border: "2px solid var(--c-ink)",
                borderRadius: 999,
                fontSize: 16,
                background: "var(--c-paper)",
                color: "var(--c-ink)",
                fontFamily: "var(--f-body)",
              }}
            />
            <button type="submit" className="btn ochre" disabled={submitting}>
              {submitting ? "Adding you…" : "Keep me posted"}
            </button>
          </form>
        ) : (
          <div className="card" style={{ marginTop: 28, maxWidth: 520, background: "var(--c-paper)", color: "var(--c-ink)", padding: 22 }}>
            <div className="display" style={{ fontSize: 24 }}>You&rsquo;re on the list</div>
            <div style={{ fontSize: 14, marginTop: 8, color: "var(--c-ink-soft)", lineHeight: 1.5 }}>
              We&rsquo;ll email you when {year} registration opens. Nothing else in the meantime.
            </div>
          </div>
        )}
        {error && (
          <div role="alert" style={{ marginTop: 12, fontSize: 14, background: "var(--c-paper)", color: "var(--c-primary-deep)", display: "inline-block", padding: "8px 14px", borderRadius: 999 }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 14, marginTop: 32, flexWrap: "wrap" }}>
          <a href="#sites" onClick={scrollTo("sites")} className="btn ochre">Browse 6 sites</a>
          <a href="#process" onClick={scrollTo("process")} className="btn secondary">How it works</a>
        </div>
      </div>
    </section>
  );
}

function CampProcess() {
  const steps = [
    { n: "01", title: "Register online", body: "Pick your site and fill out the registration form, including the City waivers, signed on screen. You get a copy by email." },
    { n: "02", title: "Pay the registration fee", body: "Your registration is pending until the Performing Arts office receives the registration fee. Your confirmation email says how to reach us to pay." },
    { n: "03", title: "You're registered", body: "Once the fee is in, we email you that your camper is registered, with a receipt." },
    { n: "04", title: "First day of camp", body: "Arrive at 9am ready to sing, dance, and act. Day one is orientation, warm-ups, and meeting your ensemble. Lunch and snack are provided." },
  ];
  return (
    <section id="process" className="section" style={{ background: "var(--c-cream)" }}>
      <div className="container">
        <SectionHeader eyebrow="The Camp Process" title="From sign-up to showtime." subtitle="Here's exactly what happens after you hit submit, and what to expect on day one." />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }} className="process-grid">
          {steps.map((s) => (
            <div key={s.n} className="card" style={{ padding: 24 }}>
              <div className="display" style={{ fontSize: 56, color: "var(--c-primary)", lineHeight: 1, marginBottom: 12 }}>{s.n}</div>
              <div className="display" style={{ fontSize: 22, marginBottom: 8 }}>{s.title}</div>
              <p style={{ fontSize: 14.5, color: "var(--c-ink-soft)", lineHeight: 1.55 }}>{s.body}</p>
            </div>
          ))}
        </div>
        <style>{`
          @media (max-width: 980px) { .process-grid { grid-template-columns: 1fr 1fr !important; } }
          @media (max-width: 540px) { .process-grid { grid-template-columns: 1fr !important; } }
        `}</style>
      </div>
    </section>
  );
}

function CampGrid({ sites, onRegister }) {
  const [filter, setFilter] = React.useState("all");
  const list = sites || window.CAMP_SITES || [];
  const filtered = filter === "all" ? list : list.filter(c => c.type === filter);

  return (
    <section id="sites" className="section" style={{ background: "var(--c-paper)", borderTop: "2px solid var(--c-ink)", borderBottom: "2px solid var(--c-ink)" }}>
      <div className="container">
        <SectionHeader
          eyebrow="All 6 Sites"
          title="Pick your neighborhood."
          right={
            <div style={{ display: "flex", gap: 8 }}>
              {["all", "Intro", "YPTC"].map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className="btn small"
                  style={{
                    background: filter === f ? "var(--c-ink)" : "var(--c-paper)",
                    color: filter === f ? "var(--c-paper)" : "var(--c-ink)",
                    boxShadow: filter === f ? "2px 2px 0 var(--c-primary)" : "2px 2px 0 var(--c-ink)",
                  }}
                >
                  {f === "all" ? "All sites" : f === "Intro" ? "Intro to Performing Arts (6–11)" : "Young Performers Theatre Camp (12–18)"}
                </button>
              ))}
            </div>
          }
        />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }} className="sites-grid">
          {filtered.map((camp, idx) => (
            <CampCardFull key={camp.id} camp={camp} index={idx} onRegister={() => onRegister(camp)} />
          ))}
        </div>
        <style>{`
          @media (max-width: 980px) { .sites-grid { grid-template-columns: 1fr 1fr !important; } }
          @media (max-width: 640px) { .sites-grid { grid-template-columns: 1fr !important; } }
        `}</style>
      </div>
    </section>
  );
}

function CampCardFull({ camp, index, onRegister }) {
  const tints = ["#F5C6B0", "#F2D88F", "#C9D9B0", "#E8C9D2", "#C8C0E0", "#F8D4B8"];
  const cms = window.CMS || { get: (_k, fb) => fb };
  const dates = camp.type === "Intro"
    ? cms.get("camps.intro_dates_label", "July 6 – August 14, 2026")
    : cms.get("camps.yptc_dates_label", "June 30 – August 8, 2026");
  const hours = camp.type === "Intro"
    ? cms.get("camps.intro_hours", "Mon–Fri, 9am – 2pm")
    : cms.get("camps.yptc_hours", "Mon–Fri, 9am – 2pm");
  const fee = cms.get("camps.fee_intro_dollars", 300);
  return (
    <div className="card" style={{ padding: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <div
        style={{
          background: tints[index % tints.length],
          padding: "24px 24px 16px",
          borderBottom: "2px solid var(--c-ink)",
          position: "relative",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div className="display" style={{ fontSize: 22, lineHeight: 1.05, flex: 1 }}>{camp.name}</div>
          <div className="display" style={{ fontSize: 36, color: "var(--c-ink-soft)", opacity: 0.4 }}>0{index + 1}</div>
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
          <span className={`chip ${camp.type === "Intro" ? "intro" : "yptc"}`} style={{ background: "var(--c-paper)" }}>
            {camp.type === "Intro" ? "Intro to Performing Arts" : "Young Performers Theatre Camp"}
          </span>
          <span className="chip" style={{ background: "var(--c-paper)" }}>{camp.ageRange}</span>
          {camp.registrationClosed && (
            <span className="chip" style={{ background: "var(--c-ink)", color: "var(--c-paper)" }}>Full</span>
          )}
        </div>
      </div>
      <div style={{ padding: 22, display: "flex", flexDirection: "column", gap: 14, flex: 1 }}>
        <InfoRow label="Region" value={camp.region} />
        <InfoRow label="Address" value={camp.address} />
        <InfoRow label="Dates" value={dates} />
        <InfoRow label="Hours" value={hours} />
        <InfoRow label="Fee" value={`$${fee} for the full session`} />
        {camp.type === "YPTC" && (
          <div style={{ fontSize: 12, color: "var(--c-ink-soft)", fontStyle: "italic", lineHeight: 1.4, marginTop: -6 }}>
            An audition is required to be accepted into this camp.
          </div>
        )}
        {/* Season closed outranks a single site being full -- "Registration
            full" would imply the others still have room. */}
        {!campRegistrationOpen() ? (
          <button disabled className="btn small" style={{ marginTop: "auto", width: "100%", justifyContent: "center", opacity: 0.55, cursor: "not-allowed" }}>
            Registration opens soon
          </button>
        ) : camp.registrationClosed ? (
          <button disabled className="btn small" style={{ marginTop: "auto", width: "100%", justifyContent: "center", opacity: 0.55, cursor: "not-allowed" }}>
            Registration full
          </button>
        ) : (
          <button onClick={onRegister} className="btn small" style={{ marginTop: "auto", width: "100%", justifyContent: "center" }}>
            Register for this site
          </button>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: "flex", gap: 12, fontSize: 13.5 }}>
      <div style={{
        fontFamily: "var(--f-mono)",
        fontSize: 10.5,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        color: "var(--c-ink-soft)",
        width: 70,
        flexShrink: 0,
        paddingTop: 2,
      }}>{label}</div>
      <div style={{ color: "var(--c-ink)", flex: 1 }}>{value}</div>
    </div>
  );
}

function CampFAQ() {
  const faqs = [
    { q: "What does it cost?", a: "$300 for the full Intro to Performing Arts session (six weeks). Optional aftercare until 5pm is $30/week or $150 for the whole summer (one free week if prepaid). YPTC is also $300 for the six-week session. Lunch and snack are provided daily." },
    { q: "What does my child need to bring?", a: "A water bottle, comfortable clothes they can move in, and a copy of their birth certificate. New campers also need a letter of recommendation from a teacher, neighbor, mentor, or relative who believes a performing arts camp is a good fit. We supply scripts for the final showcase." },
    { q: "What does a typical day look like?", a: "Camp runs daily from 9am to 2pm. Campers take classes in movement, drama, and music, with field trips related to the performing arts woven in. Lunch and snack are provided." },
    { q: "What happens at the end of camp?", a: "Intro campers perform at the Dell Music Center during the final week — a chance to perform in a professional space. YPTC produces a full live show at Venice Island Performing Arts Center — backed by a live band — with two evening performances on August 6 and 7, 2026." },
    { q: "Does YPTC require an audition?", a: "First-time YPTC campers audition: sing a cappella, read or act out a short text of their choosing, and submit a birth certificate copy plus a letter of recommendation from a non-relative teacher, neighbor, or mentor. Auditions can be done in person or via Zoom. Returning campers do not audition." },
    { q: "Is bus transportation provided?", a: "No — families arrange their own transportation to and from camp. (We're working on this for future summers.)" },
    { q: "I have a returning camper — anything different this year?", a: "Most staff are returning. Bill Powell has retired; your new Performing Arts Coordinator is J.J. Pospiech (Joseph.J.Pospiech@phila.gov, 215-685-3585)." },
  ];
  const [open, setOpen] = React.useState(0);
  return (
    <section className="section">
      <div className="container">
        <SectionHeader eyebrow="Questions, answered" title="The basics." />
        <div style={{ maxWidth: 880 }}>
          {faqs.map((f, i) => (
            <div
              key={i}
              style={{
                borderBottom: "2px solid var(--c-ink)",
                padding: "20px 0",
              }}
            >
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                style={{
                  width: "100%",
                  background: "none",
                  border: "none",
                  textAlign: "left",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 16,
                  padding: 0,
                }}
              >
                <span className="display" style={{ fontSize: 24 }}>{f.q}</span>
                <span style={{
                  fontFamily: "var(--f-display)",
                  fontSize: 32,
                  lineHeight: 1,
                  color: "var(--c-primary)",
                  transform: open === i ? "rotate(45deg)" : "none",
                  transition: "transform 200ms",
                }}>+</span>
              </button>
              {open === i && (
                <p style={{ fontSize: 16, color: "var(--c-ink-soft)", marginTop: 12, lineHeight: 1.55, maxWidth: 720 }}>{f.a}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CheckboxRow({ label, value, onChange }) {
  return (
    <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13.5, cursor: "pointer" }}>
      <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}


function FormStep({ title, subtitle, children }) {
  return (
    <div>
      <div className="display" style={{ fontSize: 28, marginBottom: subtitle ? 6 : 18, lineHeight: 1.1 }}>{title}</div>
      {subtitle && <p style={{ fontSize: 14, color: "var(--c-ink-soft)", marginBottom: 18 }}>{subtitle}</p>}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", options, optionLabels, required, placeholder, pattern, inputMode, maxLength, formatter, title, min, max }) {
  const handleInput = (raw) => onChange(formatter ? formatter(raw) : raw);
  return (
    <div className="field">
      <label>{label}{required && <span style={{ color: "var(--c-primary)" }}> *</span>}</label>
      {type === "textarea" ? (
        <textarea value={value || ""} onChange={e => handleInput(e.target.value)} rows={3} />
      ) : type === "select" ? (
        <select value={value || ""} onChange={e => onChange(e.target.value)}>
          {(options || []).map((o, i) => <option key={String(o) + i} value={o}>{(optionLabels && optionLabels[i]) || (o || "—")}</option>)}
        </select>
      ) : (
        <input
          type={type}
          value={value == null ? "" : value}
          onChange={e => handleInput(e.target.value)}
          required={required}
          placeholder={placeholder}
          pattern={pattern}
          inputMode={inputMode}
          maxLength={maxLength}
          title={title}
          min={min}
          max={max}
        />
      )}
    </div>
  );
}

/* CR #219: shared input mask helpers. Each formatter strips non-digits and
   re-inserts the canonical separator so the value always conforms to its
   pattern. Pair with HTML5 `pattern` + `required` for hard validation on
   submit (the browser blocks submit when the field's value doesn't match). */
function formatPhone(raw) {
  const d = String(raw || "").replace(/\D/g, "").slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}
function formatDateMDY(raw) {
  const d = String(raw || "").replace(/\D/g, "").slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}
function formatMonthYear(raw) {
  const d = String(raw || "").replace(/\D/g, "").slice(0, 4);
  if (d.length <= 2) return d;
  return `${d.slice(0, 2)}/${d.slice(2)}`;
}
function formatSSN(raw) {
  const d = String(raw || "").replace(/\D/g, "").slice(0, 9);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
}

Object.assign(window, { CampsPage, Field, FormStep, CheckboxRow, formatPhone, formatDateMDY, formatMonthYear, formatSSN });
